import { Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Input, Select, Grid, GridItem } from "@chakra-ui/react"
import { COLORS, PAYMENT_STATUS } from "@/common/constants";

import { getCurrencySymbol } from "@/utils/currency";
import { format } from "date-fns";
import { MouseEventHandler, useEffect } from "react";
import { useState } from "react";
import CurrencyInput from "../Fields/CurrencyInput";
import { CURRENCY } from "@/common/constants";
import { Form, Formik } from "formik";
import { getExchangeRateDateRange} from '@/services/exchangeRateService';

type ModalPaymentConfirmationProps = {
    isOpen: boolean,
    onClose: () => void,
    handleConfirmToCancel: MouseEventHandler<HTMLButtonElement>,
    handleConfirm: (approvalStatus: string, manualPaymentDate?: string) => Promise<void>,
    dataConfirmation: any,
    approvalStatus: string,
    handleChangeApprovalStatus: (status: string) => void,
    handleChangeNote: (note: string) => void,
    handleDownload: (paymentDownloadId: string, type?: number) => void,
    errorMessageApproval: string,
    errorMessageNote: string,
    setPaymentDate: (date: string) => void,
    amountIDR: number,
    setAmountIDR: (value: number) => void,
    isPaymentConfirmViewOnly?: boolean
}

const ModalPaymentConfirmation = ({ isOpen, onClose, handleConfirmToCancel, handleConfirm, dataConfirmation, approvalStatus, handleChangeApprovalStatus, handleChangeNote, handleDownload, errorMessageApproval, errorMessageNote, setPaymentDate, amountIDR, setAmountIDR, isPaymentConfirmViewOnly }: ModalPaymentConfirmationProps) => {

    const [latestExchangeRate, setLatestExchangeRate] = useState<any>(null);
    const [manualExchangeRate, setManualExchangeRate] = useState<string | number>("");
    const [manualPaymentDate, setManualPaymentDate] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [exchangeError, setExchangeError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (isOpen) {
            setLatestExchangeRate(null);
            setExchangeError(null);
            setIsLoading(true);
            getLatestExchangeRate();
            if (dataConfirmation?.paymentDate) {
                setPaymentDate(dataConfirmation.paymentDate);
            }
        }
    }, [isOpen, retryCount]);


    // Helper to recalculate amountIDR
    const recalculateAmountIDR = (exchangeRateValue: number | undefined) => {
        if (dataConfirmation?.currency === CURRENCY.USD.code && exchangeRateValue) {
            setAmountIDR((dataConfirmation?.amount || 0) * exchangeRateValue);
        } else if (dataConfirmation?.currency === CURRENCY.IDR.code) {
            setAmountIDR(dataConfirmation?.amount || 0);
        } else {
            setAmountIDR(0);
        }
    };

    const getLatestExchangeRate = async (customDate?: string) => {
        setIsLoading(true);
        setExchangeError(null);
        try {
            const endDate = customDate
                ? new Date(customDate)
                : dataConfirmation?.paymentDate
                    ? new Date(dataConfirmation.paymentDate)
                    : new Date(new Date().setDate(new Date().getDate() - 1));
            const end = format(endDate, 'yyyy-MM-dd');
            const startDate = new Date(endDate);
            startDate.setDate(endDate.getDate() - 15);
            const start = format(startDate, 'yyyy-MM-dd');
            const response = await getExchangeRateDateRange(start, end, 1, 10);
            if ((response.status === 200 || response.status === 'OK') && response.data) {
                setLatestExchangeRate(response.data[0]);
                const exchangeRateValue = response.data[0]?.kurs || manualExchangeRate;
                recalculateAmountIDR(exchangeRateValue);
            } else {
                setExchangeError('Failed to load exchange rate.');
                recalculateAmountIDR(undefined);
            }
        } catch {
            setExchangeError('Error loading exchange rate.');
            recalculateAmountIDR(undefined);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Modal onClose={onClose} isOpen={isOpen} isCentered size='xl'>
            <ModalOverlay />
            <Formik
                initialValues={{ status: "" }}
                validate={values => {
                    const errors: any = {};
                    if (!values.status || values.status === "-1") {
                        errors.status = "Status is required";
                    }
                    return errors;
                }}
                onSubmit={async values => {
                    await handleConfirm(values.status, manualPaymentDate)
                    handleChangeApprovalStatus(values.status)
                }}
            >
                {formik => (
                    <Form>
                        <ModalContent borderRadius={15} padding={'10px'}>
                            <ModalHeader>
                                {
                                    isPaymentConfirmViewOnly ? 'Payment Confirmation Details' : 'Payment Confirmation'
                                }
                            </ModalHeader>
                            <ModalCloseButton onClick={handleConfirmToCancel} _focus={{ outline: 'none', boxShadow: 'none' }} />
                            <ModalBody marginTop={5}>
                                <Grid templateColumns='repeat(2, 1fr)' gap={6}>
                                    <GridItem w='100%'>
                                        <p style={{ fontWeight: 'bold' }}>Invoice ID</p>
                                        <p>{dataConfirmation?.invoiceID}</p>
                                    </GridItem>
                                    <GridItem w='100%'>
                                        <p style={{ fontWeight: 'bold' }}>Company Name</p>
                                        <p>{dataConfirmation?.companyName}</p>
                                    </GridItem>

                                    <GridItem w='100%'>
                                        <p style={{ fontWeight: 'bold' }}>Vessel Name</p>
                                        <p>{dataConfirmation?.shipName}</p>
                                    </GridItem>
                                    <GridItem w='100%'>
                                        <p style={{ fontWeight: 'bold' }}>Amount</p>
                                        <p>{dataConfirmation?.currency ? getCurrencySymbol(dataConfirmation?.currency, dataConfirmation?.amount) : (dataConfirmation?.amount || '-')}</p>
                                    </GridItem>
                                    <GridItem w='100%'>
                                        <p style={{ fontWeight: 'bold' }}>Payment Date</p>
                                        <Input
                                            type="date"
                                            value={manualPaymentDate !== "" ? manualPaymentDate : (dataConfirmation?.paymentDate ? format(new Date(dataConfirmation?.paymentDate), 'yyyy-MM-dd') : "")}
                                            onChange={e => {
                                                const newDate = e.target.value;
                                                setManualPaymentDate(newDate);
                                                setManualExchangeRate(""); // Clear manual exchange rate so fetched value is used
                                                setLatestExchangeRate(null);
                                                setExchangeError(null);
                                                setIsLoading(true);
                                                getLatestExchangeRate(newDate);
                                            }}
                                            width="100%"
                                            bg="white"
                                            max={format(new Date(), 'yyyy-MM-dd')}
                                        />
                                    </GridItem>
                                    <GridItem w='100%'>
                                        <p style={{ fontWeight: 'bold' }}>Amount in IDR</p>
                                        <CurrencyInput
                                            placeholder='Amount in IDR'
                                            prefix={CURRENCY.IDR.code}
                                            value={amountIDR}
                                            onValueChange={({ floatValue = 0 }) => setAmountIDR(floatValue)}
                                            width='100%'
                                        />
                                    </GridItem>
                                    {/* Latest Exchange Rate */}
                                    <GridItem w='100%'>
                                        <p style={{ fontWeight: 'bold' }}>Latest Exchange Rate</p>
                                        <Input
                                            type="number"
                                            value={manualExchangeRate !== "" ? manualExchangeRate : (isLoading ? 'Loading...' : exchangeError ? exchangeError : (latestExchangeRate ? latestExchangeRate.kurs : ''))}
                                            onChange={e => {
                                                setManualExchangeRate(e.target.value);
                                                recalculateAmountIDR(Number(e.target.value));
                                            }}
                                            bg='white'
                                            style={{ opacity: 1 }}
                                            placeholder={isLoading ? 'Loading...' : 'Latest exchange rate'}
                                        />
                                        {exchangeError && (
                                            <Button size="sm" mt={2} onClick={() => setRetryCount(retryCount + 1)}>
                                                Retry
                                            </Button>
                                        )}
                                    </GridItem>
                                </Grid>
                                <div style={{ width: '100%', marginTop: 20 }}>
                                    {/* ...existing code... */}
                                </div>
                                {/* ...existing code... */}
                                {(dataConfirmation?.paymentDownloadId) && <div style={{ width: '100%', marginTop: 30, marginBottom: 20 }}>
                                    <p style={{ fontWeight: 'bold', marginBottom: 5 }}>Proof of Payment</p>
                                    <Button
                                        onClick={() => {
                                            if (dataConfirmation?.paymentDownloadId) {
                                                handleDownload(dataConfirmation?.paymentDownloadId)
                                            }
                                        }}
                                        style={{ width: '100%' }}
                                    >Download</Button>
                                </div>}
                                {!isPaymentConfirmViewOnly && 
                                    (
                                        <div style={{ marginTop: 20 }}>
                                            <p style={{ marginBottom: 10, fontWeight: 'bold' }}>Status <span style={{ color: 'red' }}>*</span></p>
                                            <Select name="status" fontSize="12px" cursor="pointer" onChange={formik.handleChange} value={formik.values.status} borderColor={((errorMessageApproval || formik.errors.status) ? 'red' : 'gray')} required>
                                                <option key='none' value={-1}>Please select...</option>
                                                <option key='approved' value={PAYMENT_STATUS.COMPLETED}>Approve</option>
                                                <option key='rejected' value={PAYMENT_STATUS.REJECTED}>Reject</option>
                                            </Select>
                                            {(errorMessageApproval || formik.errors.status) && <p style={{ fontSize: '14px', color: 'red' }}>{errorMessageApproval || formik.errors.status}</p>}
                                            {approvalStatus === '3' && <>
                                                <p style={{ marginTop: 5, marginBottom: 10, fontWeight: 'bold' }}>Note</p>
                                                <Input placeholder="Input your note here" onChange={(e) => handleChangeNote(e.target.value)} borderColor={errorMessageNote ? 'red' : 'gray'} />
                                                {errorMessageNote && <p style={{ fontSize: '14px', color: 'red' }}>{errorMessageNote}</p>}
                                            </>}
                                        </div>
                                    )
                                }
                            </ModalBody>
                            {!isPaymentConfirmViewOnly &&
                                (
                                    <ModalFooter style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: 10 }}>
                                        <Button onClick={handleConfirmToCancel} borderColor='#D0D5DD' bgColor='white'>Cancel</Button>
                                        <Button type="submit" isLoading={formik.isSubmitting} disabled={formik.isSubmitting} loadingText="Submitting" bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND}>Submit</Button>
                                    </ModalFooter>
                                )
                            }
                        </ModalContent>
                    </Form>
                )}
            </Formik>
        </Modal>
    )
}

export default ModalPaymentConfirmation;